import posthog from "posthog-js";
import type { AnalyticsContextResponse } from "./api/analytics-context";
import { validateIntakeAnalyticsProperties } from "@batch-and-bowl/shared";
import { PROTEIN_TAG_VALUES } from "@batch-and-bowl/validators";
import type {
  AnalyticsArguments,
  AnalyticsEvent,
  ServerAnalyticsEvent,
  CandidateMode,
  MealType,
} from "@batch-and-bowl/shared";
export type {
  AnalyticsArguments,
  AnalyticsEvent,
  AnalyticsEventProperties,
  AnalyticsView,
  ServerAnalyticsEvent,
} from "@batch-and-bowl/shared";
import { validateAnalyticsProperties } from "@batch-and-bowl/shared";

/**
 * Correction-reason taxonomy for a pantry review edit (issue #742 step 3;
 * needed for edit-rate to be a valid extraction-accuracy proxy -- otherwise
 * it conflates "the model was wrong" with "I just prefer it differently").
 * Shipped as a type now, deliberately NOT wired into any track() call: the
 * review UI (PantryIntake.tsx) has no per-row place to capture it yet, and
 * #774/#775 are already mid-rewrite of that component's row state. A
 * follow-up slice adds a per-row `editReason` picker and threads it into
 * `pantry_intake_reviewed` once that UI settles.
 */
export type PantryEditReason =
  "wrong_name" | "wrong_quantity" | "wrong_unit" | "duplicate" | "preference";

let ready = false;
const PROJECT_KEY_PREFIX = "phc_";
const PROJECT_KEY_PATTERN = /^phc_\S+$/;

export function initAnalytics(key: string | undefined): void {
  if (!key || ready) return;
  if (!PROJECT_KEY_PATTERN.test(key)) {
    console.warn(
      `[analytics] Ignoring NEXT_PUBLIC_POSTHOG_KEY: expected a non-empty PostHog project API key matching "${PROJECT_KEY_PREFIX}..." with no whitespace (PostHog -> Settings -> Project -> Project API Key). Analytics is disabled.`,
    );
    return;
  }
  try {
    posthog.init(key, {
      api_host: "https://us.i.posthog.com",
      // Cookies are unnecessary for our explicitly identified, same-origin
      // events. The SDK probes public suffixes even with host-only cookies
      // during cookie cleanup; localStorage avoids that rejected-cookie noise.
      persistence: "localStorage",
      cross_subdomain_cookie: false,
      capture_pageview: false,
      autocapture: false,
      disable_session_recording: true,
    });
    ready = true;
  } catch {
    // Analytics is observability, never a prerequisite for product actions.
  }
}

export function setAnalyticsIdentity(
  userId: string,
  context: AnalyticsContextResponse,
): void {
  if (!ready) return;
  try {
    posthog.identify(
      userId,
      {
        householdAdults: context.householdAdults,
        householdKids: context.householdKids,
        skillLevel: context.skillLevel,
        analyticsCohort: context.analyticsCohort,
      },
      { signupWeek: context.signupWeek },
    );
  } catch {
    // Identity enrichment is best-effort for the same reason as capture.
  }
  try {
    if (context.householdId) posthog.group("household", context.householdId);
    else posthog.resetGroups();
  } catch {
    // Household cleanup must be attempted independently of identification.
  }
}

/** Backwards-compatible identity helper for callers without context. */
export function identifyUser(userId: string): void {
  if (!ready) return;
  try {
    posthog.identify(userId);
  } catch {
    /* best effort */
  }
}

export function clearAnalyticsIdentity(): void {
  if (!ready) return;
  resetPostHogIdentity();
}

function resetPostHogIdentity(): void {
  try {
    posthog.reset();
  } catch {
    /* best effort */
  }
  try {
    posthog.resetGroups();
  } catch {
    /* best effort */
  }
}

export function resetAnalytics(): void {
  if (!ready) return;
  resetPostHogIdentity();
}

type GuidedAnalyticsEvent =
  | "plan_recommendation_batch_shown"
  | "plan_recommendation_selection_changed"
  | "plan_recommendation_committed"
  | "plan_preview_generated"
  | "plan_preview_refreshed"
  | "plan_candidate_menu_committed"
  | "plan_candidate_menu_dismissed"
  | "pantry_scan_attempted"
  | "pantry_scan_succeeded"
  | "pantry_scan_failed"
  | "pantry_intake_reviewed"
  | "recipe_import_reviewed"
  | "onboarding_step_completed";

/** Onboarding v3 (issue #831): the closed six-step enum, mirrored from
 * `OnboardingStepId` (apps/web/lib/preference-effects.ts) without importing
 * it -- this module stays a leaf so every event validator can stay a
 * synchronous, dependency-light check. */
const ONBOARDING_STEP_INDEX_BY_ID = {
  safety: 1,
  household: 2,
  tastes: 3,
  cooking: 4,
  budget: 5,
  review: 6,
} as const;
const ONBOARDING_STEP_ID_VALUES = new Set(
  Object.keys(ONBOARDING_STEP_INDEX_BY_ID),
);

const CANDIDATE_MODES = new Set<CandidateMode>(["create", "fill", "replace"]);
const MEAL_TYPES = new Set<MealType>(["breakfast", "lunch", "dinner", "snack"]);
const CANDIDATE_STEPS = new Set([
  "configure",
  "breakfast",
  "lunch",
  "dinner",
  "snack",
  "review",
]);
const PROTEIN_TAG_VALUE_SET = new Set<string>(PROTEIN_TAG_VALUES);

function isAggregateCount(value: unknown): value is number {
  return Number.isInteger(value) && (value as number) >= 0;
}

function isRecommendationContext(props: Record<string, unknown>): boolean {
  return typeof props.sessionId === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(props.sessionId) &&
    typeof props.algorithmVersion === "string" && /^[a-z0-9._-]{1,64}$/.test(props.algorithmVersion);
}

function isRecipeId(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= 128;
}

function isRecipeIds(value: unknown): value is string[] {
  return Array.isArray(value) && value.length <= 28 && value.every(isRecipeId) &&
    new Set(value).size === value.length;
}

function hasExactKeys(
  props: Record<string, unknown>,
  expectedKeys: readonly string[],
): boolean {
  const keys = Object.keys(props);
  return (
    keys.length === expectedKeys.length &&
    expectedKeys.every((key) => Object.hasOwn(props, key))
  );
}

const GUIDED_EVENT_VALIDATORS: Record<
  GuidedAnalyticsEvent,
  (props: Record<string, unknown>) => boolean
> = {
  plan_recommendation_batch_shown: (props) =>
    hasExactKeys(props, ["sessionId", "algorithmVersion", "mealType", "batchIndex", "recipeIds", "eligibleCount", "unseenCount", "freshCount", "pantryMatchCount", "selectedCount", "exhausted"]) &&
    isRecommendationContext(props) && MEAL_TYPES.has(props.mealType as MealType) &&
    isAggregateCount(props.batchIndex) && isRecipeIds(props.recipeIds) &&
    isAggregateCount(props.eligibleCount) && isAggregateCount(props.unseenCount) &&
    props.unseenCount <= props.eligibleCount &&
    isAggregateCount(props.freshCount) && props.freshCount <= props.recipeIds.length &&
    isAggregateCount(props.pantryMatchCount) && props.pantryMatchCount <= props.recipeIds.length &&
    isAggregateCount(props.selectedCount) && typeof props.exhausted === "boolean",
  plan_recommendation_selection_changed: (props) =>
    hasExactKeys(props, ["sessionId", "algorithmVersion", "mealType", "recipeId", "position", "selected", "pantryOverlap"]) &&
    isRecommendationContext(props) && MEAL_TYPES.has(props.mealType as MealType) &&
    isRecipeId(props.recipeId) && isAggregateCount(props.position) && props.position > 0 && props.position <= 28 &&
    typeof props.selected === "boolean" && typeof props.pantryOverlap === "boolean",
  plan_recommendation_committed: (props) =>
    hasExactKeys(props, ["sessionId", "algorithmVersion", "mealPlanId", "recipeIds"]) &&
    isRecommendationContext(props) && isRecipeId(props.mealPlanId) &&
    isRecipeIds(props.recipeIds) && props.recipeIds.length > 0,
  plan_preview_generated: (props) =>
    hasExactKeys(props, [
      "mode",
      "enabledMealTypeCount",
      "targetCount",
      "returnedCount",
    ]) &&
    CANDIDATE_MODES.has(props.mode as CandidateMode) &&
    isAggregateCount(props.enabledMealTypeCount) &&
    isAggregateCount(props.targetCount) &&
    isAggregateCount(props.returnedCount),
  plan_preview_refreshed: (props) =>
    hasExactKeys(props, [
      "mealType",
      "selectedCount",
      "freshCount",
      "exhausted",
      "proteinFilter",
    ]) &&
    MEAL_TYPES.has(props.mealType as MealType) &&
    isAggregateCount(props.selectedCount) &&
    isAggregateCount(props.freshCount) &&
    typeof props.exhausted === "boolean" &&
    Array.isArray(props.proteinFilter) &&
    props.proteinFilter.every(
      (value) => typeof value === "string" && PROTEIN_TAG_VALUE_SET.has(value),
    ),
  plan_candidate_menu_committed: (props) =>
    hasExactKeys(props, [
      "mode",
      "selectedCount",
      "openCount",
      "mealTypeCount",
    ]) &&
    CANDIDATE_MODES.has(props.mode as CandidateMode) &&
    isAggregateCount(props.selectedCount) &&
    isAggregateCount(props.openCount) &&
    isAggregateCount(props.mealTypeCount),
  plan_candidate_menu_dismissed: (props) =>
    hasExactKeys(props, ["lastStep", "selectedCount"]) &&
    CANDIDATE_STEPS.has(props.lastStep as string) &&
    isAggregateCount(props.selectedCount),
  pantry_scan_attempted: (props) => validateIntakeAnalyticsProperties("pantry_scan_attempted", props),
  pantry_scan_succeeded: (props) => validateIntakeAnalyticsProperties("pantry_scan_succeeded", props),
  pantry_scan_failed: (props) => validateIntakeAnalyticsProperties("pantry_scan_failed", props),
  pantry_intake_reviewed: (props) => validateIntakeAnalyticsProperties("pantry_intake_reviewed", props),
  recipe_import_reviewed: (props) => validateIntakeAnalyticsProperties("recipe_import_reviewed", props),
  onboarding_step_completed: (props) =>
    hasExactKeys(props, ["stepId", "stepIndex", "answeredCount", "skipped"]) &&
    ONBOARDING_STEP_ID_VALUES.has(props.stepId as string) &&
    isAggregateCount(props.stepIndex) &&
    ONBOARDING_STEP_INDEX_BY_ID[
      props.stepId as keyof typeof ONBOARDING_STEP_INDEX_BY_ID
    ] === props.stepIndex &&
    isAggregateCount(props.answeredCount) &&
    typeof props.skipped === "boolean",
};

function isGuidedAnalyticsEvent(
  event: AnalyticsEvent,
): event is GuidedAnalyticsEvent {
  return Object.hasOwn(GUIDED_EVENT_VALIDATORS, event);
}

export type ClientAnalyticsEvent = Exclude<
  AnalyticsEvent,
  ServerAnalyticsEvent
>;

export function track<E extends ClientAnalyticsEvent>(
  ...args: AnalyticsArguments<E>
): void {
  if (!ready) return;
  const [event, props] = args as [ClientAnalyticsEvent, unknown?];
  try {
    if (
      isGuidedAnalyticsEvent(event) &&
      (!props ||
        !GUIDED_EVENT_VALIDATORS[event](props as Record<string, unknown>))
    ) {
      console.warn(`[analytics] Dropped ${event}: invalid properties`);
      return;
    }
    if (!validateAnalyticsProperties(props)) {
      console.warn(`[analytics] Dropped ${event}: private property key`);
      return;
    }
    posthog.capture(event, props as Record<string, unknown>);
  } catch {
    // A telemetry provider failure must never interrupt the product lifecycle.
  }
}
